import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';

/**
 * Fetches the price for a given article from WeClapp based on customer, date, and quantity.
 * @param {object} params - The parameters for the price fetch.
 * @param {string} params.articleId - The ID of the article.
 * @param {string} params.customerId - The WeClapp customer ID.
 * @param {Date} params.pricingDate - The date for which to find the price.
 * @param {number} params.quantity - The quantity to check against price scales.
 * @param {object} params.weclappApiUrl - The base URL for the WeClapp proxy.
 * @returns {Promise<number|null>} The price found, or null if none was found.
 */
export const fetchWeclappPrice = async ({
  articleId,
  customerId,
  pricingDate,
  quantity,
  weclappApiUrl,
}) => {
  if (!articleId || !customerId || !weclappApiUrl) {
    return null;
  }

  const validPricingDate =
    pricingDate instanceof Date && !isNaN(pricingDate)
      ? pricingDate
      : new Date();

  try {
    const {
      data: { result: priceResults },
    } = await proxy.get(
      `${weclappApiUrl}/articlePrice?articleId-eq=${articleId}`,
    );
    const foundPrice = priceResults
      .filter((priceItem) => {
        const isForCustomer =
          !priceItem.customerId || priceItem.customerId === customerId;
        const isActiveOnDate =
          (!priceItem.startDate ||
            validPricingDate >= new Date(priceItem.startDate)) &&
          (!priceItem.endDate ||
            validPricingDate <= new Date(priceItem.endDate));
        const isInScaleRange =
          (priceItem.priceScaleType === 'SCALE_FROM' &&
            priceItem.priceScaleValue <= quantity) ||
          (priceItem.priceScaleType === 'SCALE_TO' &&
            priceItem.priceScaleValue >= quantity) ||
          !priceItem.priceScaleType;
        return isForCustomer && isActiveOnDate && isInScaleRange;
      })
      .sort((a, b) => {
        if (a.customerId && !b.customerId) return -1;
        if (b.customerId && !a.customerId) return 1;
        if (a.priceScaleType === 'SCALE_FROM')
          return b.priceScaleValue - a.priceScaleValue;
        if (a.priceScaleType === 'SCALE_TO')
          return a.priceScaleValue - b.priceScaleValue;
        return 0;
      })[0];

    let discountPercentage = 0;
    if (foundPrice && foundPrice.reductionAdditions?.length > 0) {
      const basePrice = Number(foundPrice.price);

      foundPrice.reductionAdditions.forEach((addition) => {
        const value = Number(addition.value);

        switch (addition.type) {
          case 'ADDITION_PERCENT':
            discountPercentage += value;
            break;

          case 'REDUCTION_PERCENT':
            discountPercentage -= value;
            break;

          case 'ADDITIONAL_ABS':
            if (basePrice !== 0) {
              discountPercentage += (value / basePrice) * 100;
            }
            break;

          case 'REDUCTION_ABS':
            if (basePrice !== 0) {
              discountPercentage -= (value / basePrice) * 100;
            }
            break;

          default:
            break;
        }
      });
    }

    discountPercentage = Math.round(discountPercentage * 1000) / 1000;
    return {
      price: foundPrice ? Number(foundPrice.price) : null,
      discountPercentage,
    };
  } catch (error) {
    toast.error(`Error fetching price for article ${articleId}:`, error);
    return null;
  }
};
